import React, {useCallback} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {buildOfferContext} from 'middleware/analytics/contexts/offerContext'
import {
  getAlternativeOffersList,
  getOffersEntityWithoutVioOffers
} from 'modules/alternativeOffers/selectors'
import {getAnonymousId, getLanguageCode} from 'modules/meta/selectors'
import {getShowTotalPrices} from 'modules/sapiSearch/selectors'

import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {Action, Category, Entity} from '@vio/core/src/analytics/types/Events'
import {SearchOffer} from '@vio/core/src/offer/types/SearchOffer'
import {AlternativeOffersContext} from '@vio/shared/src/booking/AlternativeOffers/AlternativeOffersContext'
import {AlternativeOffersList} from '@vio/shared/src/booking/AlternativeOffers/AlternativeOffersList'

/**
 * Builds the offer context for the alternative offers list.
 * ! Contained logic to be used only in the context of the alternative offers list.
 * @returns offerContextBuilder
 */
const useGetAlternativeOfferContextBuilder = () => {
  const offersEntityforTracking = useSelector(getOffersEntityWithoutVioOffers)

  const offerContextBuilder = useCallback(
    (offer: SearchOffer) => {
      return buildOfferContext(
        offer.id,
        'APAlternativeOffersOverlay',
        offersEntityforTracking,
        offer,
        1,
        false,
        null, //we don't have split booking details in the alternative offers list
        null //we don't have offer access flow settings in the alternative offers list
      )
    },
    [offersEntityforTracking]
  )

  return {
    offerContextBuilder
  }
}

export const AlternativeOffersListContainer = () => {
  const offers = useSelector(getAlternativeOffersList)

  const dispatch = useDispatch()
  const anonymousId = useSelector(getAnonymousId)

  const showTotalPrices = useSelector(getShowTotalPrices) //check if its the right place
  const lang = useSelector(getLanguageCode)

  const {offerContextBuilder} = useGetAlternativeOfferContextBuilder()

  const onOfferClick = (offer: SearchOffer) => {
    dispatch(
      trackEvent({
        action: Action.Clicked,
        category: Category.User,
        entity: Entity.Offer,
        analyticsContext: {
          offerContext: offerContextBuilder(offer)
        }
      })
    )
  }

  const onOfferViewed = (offer: SearchOffer) => {
    dispatch(
      trackEvent({
        action: Action.Viewed,
        entity: Entity.Offer,
        category: Category.User,
        analyticsContext: {
          offerContext: offerContextBuilder(offer)
        }
      })
    )
  }

  return (
    <AlternativeOffersContext.Provider
      value={{
        showTotalPrices,
        onOfferClick,
        lang,
        anonymousId,
        onOfferViewed
      }}
    >
      <AlternativeOffersList offers={offers} />
    </AlternativeOffersContext.Provider>
  )
}
